import React, {useEffect, useState} from "react";
import {Box, CircularProgress} from "@mui/material";
import NavBar from "./components/NavBar";
import AuthDialog from "./components/AuthDialog";
import Landing from "./pages/Landing";
import SbomDocuments from "./pages/SbomDocuments";
import SbomDocument from "./pages/SbomDocument";
import Lots from "./pages/Lots";
import Lot from "./pages/Lot";
import Shares from "./pages/Shares";
import ShareDetail from "./pages/ShareDetail";
import {docsLinkForShare, PublishedDocument, ReleasedLot, SESSION_ENDED, Share} from "./util/api";
import {Invitation} from "./pages/ForgotPassword";
import {clearSession, isSignedIn, sessionExpired} from "./util/SessionContext";
import {Offered, QMS_ITEMS, QmsItem, SECTIONS, SHARE_KIND, Section, available} from "./util/sections";

// The Customer portal. A title bar, a strip of headers beneath it, and one page at a time; signing in happens in a
// dialog over it, which opens by itself for a signed-out visitor.
//
// ONE HEADER PER APPLICATION THINGS ARE SHARED FROM (util/sections.ts): QMS (t1), Parkour-AI Documents (t11), SBOM (t4).
// Under QMS a sidebar lists the kinds of QMS object shared -- projects, folders, lots. A header or sidebar entry with
// nothing under it is not shown.

// An invitation arrives as ?invite=<key>.<code>, the two halves the reset endpoint already expects.
// It is read ONCE, at mount, and stripped from the address bar immediately: the key is single use,
// so leaving it in the URL only invites a refresh to replay a spent invitation and read as broken.
function readInvitation(): Invitation | undefined {
    const value = new URLSearchParams(window.location.search).get("invite");
    if (!value) return undefined;

    const separator = value.indexOf(".");
    if (separator <= 0 || separator === value.length - 1) return undefined;

    window.history.replaceState({}, "", window.location.pathname);

    return {key: value.slice(0, separator), code: value.slice(separator + 1)};
}

const GREY2 = "#EDEFF3";
const BLUE3 = "#1498D8";
const MUTED = "#717171";
const HEADING = "#2B2B2B";

// A plain strip rather than MUI Tabs: a handful of items, no overflow, no scroll buttons, and it has to
// sit flush under the 50px bar the staff applications also use.
function Sections({offered, current, onChange}: {offered: Section[]; current: Section; onChange: (one: Section) => void}) {
    const items = SECTIONS.filter(one => offered.includes(one.key)).map(one => [one.key, one.label] as const);
    if (items.length === 0) return null;
    return (
        <Box component="nav" aria-label="Sections"
             sx={{display: "flex", gap: 3, px: 3, flexShrink: 0,
                  borderBottom: `1px solid ${GREY2}`, backgroundColor: "#FFFFFF"}}>
            {items.map(([key, label]) => (
                <Box key={key} component="button" type="button"
                     aria-current={current === key ? "page" : undefined}
                     onClick={() => onChange(key)}
                     sx={{
                         border: "none", background: "none", cursor: "pointer", font: "inherit",
                         fontSize: 14, py: 1.5, px: 0.5,
                         color: current === key ? HEADING : MUTED,
                         fontWeight: current === key ? 600 : 400,
                         borderBottom: `2px solid ${current === key ? BLUE3 : "transparent"}`,
                         marginBottom: "-1px",
                         "&:hover": {color: HEADING},
                     }}>
                    {label}
                </Box>
            ))}
        </Box>
    );
}

// UNDER QMS, THE KINDS OF QMS OBJECT SHARED, each with how many -- and only the kinds with something in them.
function QmsSidebar({items, current, onChange}: {items: Offered["qms"]; current: QmsItem;
                                                 onChange: (one: QmsItem) => void}) {
    return (
        <Box component="nav" aria-label="Foundation"
             sx={{width: 220, flexShrink: 0, borderRight: `1px solid ${GREY2}`, backgroundColor: "#FAFBFC", py: 2}}>
            {items.map(({key, count}) => {
                const label = QMS_ITEMS.find(one => one.key === key)?.label ?? key;
                const chosen = current === key;
                return (
                    <Box key={key} component="button" type="button" aria-current={chosen ? "page" : undefined}
                         onClick={() => onChange(key)}
                         sx={{display: "flex", alignItems: "center", width: "100%", border: "none", cursor: "pointer",
                              font: "inherit", fontSize: 14, textAlign: "left", px: 3, py: 1.1,
                              color: chosen ? HEADING : MUTED, fontWeight: chosen ? 600 : 400,
                              backgroundColor: chosen ? "#EAF5FC" : "transparent",
                              borderLeft: `3px solid ${chosen ? BLUE3 : "transparent"}`,
                              "&:hover": {color: HEADING, backgroundColor: chosen ? "#EAF5FC" : "#F1F3F6"}}}>
                        <Box component="span" sx={{flex: 1}}>{label}</Box>
                        {count !== null && <Box component="span" sx={{fontSize: 12, color: MUTED}}>{count}</Box>}
                    </Box>
                );
            })}
        </Box>
    );
}

export default function App() {
    const [invitation] = useState<Invitation | undefined>(readInvitation);
    const [section, setSection] = useState<Section | null>(null);
    // null while the headers are being asked; no sections when nothing at all has been shared.
    const [offered, setOffered] = useState<Offered | null>(null);
    const [qmsItem, setQmsItem] = useState<QmsItem | null>(null);
    // Which record is open in each section, or none. Kept apart so that leaving a lot to read an
    // SBOM and coming back does not lose your place.
    const [openDocument, setOpenDocument] = useState<PublishedDocument | null>(null);
    const [openLot, setOpenLot] = useState<ReleasedLot | null>(null);
    // One open share per page, so moving between Projects and Folders keeps each one's place.
    const [openShare, setOpenShare] = useState<Partial<Record<Section | QmsItem, Share>>>({});
    // The page on screen: a QMS sidebar entry under QMS, otherwise the header itself.
    const page: Section | QmsItem | null = section === "qms" ? qmsItem : section;
    const shareKind = page ? SHARE_KIND[page] : undefined;

    // An invitation names one person, who is not necessarily whoever this browser is already signed
    // in as -- a shared machine, or a second contact at the same company. Following the link
    // therefore ends the current session rather than being swallowed by it, which is also what stops
    // the link doing nothing at all for someone who happens to be signed in.
    const [signedIn, setSignedIn] = useState<boolean>(() => {
        if (invitation) {
            clearSession();
            return false;
        }
        // A SESSION THAT RAN OUT WHILE THE TAB WAS CLOSED is ended here, before any request is refused for it.
        if (isSignedIn() && sessionExpired()) {
            clearSession();
            return false;
        }
        return isSignedIn();
    });
    const [notice, setNotice] = useState<string | null>(null);
    const [authOpen, setAuthOpen] = useState<boolean>(() => !!invitation || !isSignedIn());

    useEffect(() => {
        if (!signedIn) return;
        let current = true;
        setOffered(null);
        available().then(found => {
            if (!current) return;
            setOffered(found);
            setSection(found.sections[0] ?? null);
            setQmsItem(found.qms[0]?.key ?? null);
        });
        return () => { current = false; };
    }, [signedIn]);

    // A PARKOURAI DOCUMENT OPENS IN DOCS, in a tab of its own so the portal stays where it was. The tab is opened at
    // once, inside the click, and pointed at Docs when the link arrives -- a window opened after an await is one the
    // browser's popup blocker is entitled to refuse.
    const [docsProblem, setDocsProblem] = useState<string | null>(null);
    const openInDocs = async (one: Share, draft = false) => {
        setDocsProblem(null);
        const tab = window.open("about:blank", "_blank");
        const result = await docsLinkForShare(one.USID, draft);
        if (result.status === 200 && result.data?.Url) {
            if (tab) tab.location.href = result.data.Url; else window.location.href = result.data.Url;
            return;
        }
        tab?.close();
        setDocsProblem(result.data?.Cause || "That document could not be opened in Parkour-AI Docs.");
    };

    const signOut = () => {
        clearSession();
        setSignedIn(false);
        setAuthOpen(true);
        // NOTHING STAYS ON SCREEN THROUGH A SIGN-OUT. A record left rendered behind the dialog is
        // somebody else's SBOM -- or somebody else's certificate -- on a shared machine. Both
        // sections are cleared, which is the line that has to be revisited every time one is added.
        setOpenDocument(null);
        setOpenLot(null);
        setOpenShare({});
        setSection(null);
        setQmsItem(null);
        setOffered(null);
    };

    // THE SESSION ENDED UNDER THEM: the same clean sign-out as the menu's, with the reason said in the dialog.
    useEffect(() => {
        const ended = () => {
            setNotice("Your session ended -- they last an hour. Sign in again to carry on where you were.");
            signOut();
        };
        window.addEventListener(SESSION_ENDED, ended);
        return () => window.removeEventListener(SESSION_ENDED, ended);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    return (
        <Box sx={{height: "100%", display: "flex", flexDirection: "column"}}>
            <NavBar signedIn={signedIn} onSignIn={() => setAuthOpen(true)} onSignOut={signOut}/>

            {signedIn && offered && section && <Sections offered={offered.sections} current={section} onChange={setSection}/>}

            <Box sx={{flex: 1, minHeight: 0, display: "flex"}}>
            {signedIn && offered && section === "qms" && qmsItem && (
                <QmsSidebar items={offered.qms} current={qmsItem} onChange={setQmsItem}/>
            )}
            <Box sx={{flex: 1, minWidth: 0, overflow: "auto"}}>
                {/* SIGNED OUT, THE LANDING PAGE STILL SAYS WHAT THIS SITE IS, for the visitor who
                    dismisses the sign-in dialog to read it. Signed in, it is replaced entirely:
                    a workspace that still says "your workspace is being set up" once there is
                    something in it would be the first thing a customer disbelieved. */}
                {!signedIn ? <Landing signedIn={false}/>
                    : offered === null
                        ? <Box sx={{display: "flex", justifyContent: "center", py: 10}}><CircularProgress size={24}/></Box>
                    : page === null ? <Landing signedIn/>
                    : shareKind
                        ? (openShare[page]
                            ? <ShareDetail summary={openShare[page]!} onBack={() => setOpenShare({...openShare, [page]: undefined})}/>
                            : <Shares kind={shareKind} problem={page === "documents" ? docsProblem : null}
                                      // A DOCUMENT OPENS IN DOCS when its share lets it be read as rendered; one whose
                                      // level does not opens like a folder or project, as its record and files.
                                      onOpen={one => page === "documents" && one.May?.Render !== false
                                          ? openInDocs(one) : setOpenShare({...openShare, [page]: one})}
                                      onReview={page === "documents" ? one => openInDocs(one, true) : undefined}/>)
                    : page === "sbom"
                        ? (openDocument
                            ? <SbomDocument summary={openDocument} onBack={() => setOpenDocument(null)}/>
                            : <SbomDocuments onOpen={setOpenDocument}/>)
                        : (openLot
                            ? <Lot summary={openLot} onBack={() => setOpenLot(null)}/>
                            : <Lots onOpen={setOpenLot}/>)}
            </Box>
            </Box>

            <AuthDialog open={authOpen && !signedIn}
                        onClose={() => setAuthOpen(false)}
                        onSignedIn={() => { setSignedIn(true); setAuthOpen(false); setNotice(null); }}
                        invitation={invitation} notice={notice}/>
        </Box>
    );
}
